import { useRouter } from 'next/router'

import { Product } from '@mc/api'
import { useBogo } from '@mc/bogo-utils'
import {
  useCollapsedPaintedDoorExperiment,
  useCollapsedPaintedDoorV2Experiment,
} from '@mc/checkout'
import type { Section } from '@mc/faq'
import { useFreeTrial } from '@mc/free-trial'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'

import {
  getBundlePricingSection,
  getOnCallSection,
} from 'experiments/prcng-568_v1_bundle-ad-landing'

import { getBogoSection } from '../data/getBogoSection'
import { getFreeTrialSection } from '../data/getFreeTrialSection'
import { getGeneralSection } from '../data/getGeneralSection'
import { getGiftingFaqSection } from '../data/getGiftingSection'
import { getGuestPassSection } from '../data/getGuestPassSection'
import { getPricingSection } from '../data/getPricingSection'
import { getSessionsSection } from '../data/getSessionsSection'

export type SectionsOptions = Array<
  | 'general'
  | 'pricing'
  | 'sessions'
  | 'bogo'
  | 'promo'
  | 'free-trial'
  | 'guest-pass'
  | 'gifting'
  | 'on-call' // prcng-568_v1
  | 'bundle-&-pricing' // prcng-568_v1
>

export const useFaqData = ({
  personaFAQ = false,
  sections = ['general', 'pricing', 'sessions'],
}: {
  personaFAQ?: boolean
  sections?: SectionsOptions
}) => {
  const { pathname } = useRouter()
  const isCheckoutPage = pathname.startsWith(paths.checkout)

  const { annualPass } = useStaticData<{ annualPass: Product }>()
  const {
    isOn: isEntryPaintedDoorOnV1,
    isVariant: isEntryPaintedDoorVariantV1,
  } = useCollapsedPaintedDoorExperiment()
  const {
    isOn: isEntryPaintedDoorOnV2,
    isVariant: isEntryPaintedDoorVariantV2,
  } = useCollapsedPaintedDoorV2Experiment()
  const isEntryPaintedDoorOn = isEntryPaintedDoorOnV1 || isEntryPaintedDoorOnV2
  const isEntryPaintedDoorVariant =
    isEntryPaintedDoorVariantV1 || isEntryPaintedDoorVariantV2
  const { isBogoOfferEligible } = useBogo()
  const { trial } = useFreeTrial()

  const generalSection = getGeneralSection({
    isEntryPaintedDoorOn,
    personaFAQ,
  })
  const pricingSection = getPricingSection({
    isCheckoutPage,
    annualPass,
    isEntryPaintedDoorVariant,
  })
  const sessionsSection = getSessionsSection({ isEntryPaintedDoorVariant })
  const bogoSection = getBogoSection()
  const guestPassSection = getGuestPassSection({
    trial,
  })
  const freeTrialSection = getFreeTrialSection({ trial })
  const giftSection = getGiftingFaqSection()
  // prcng-568_v1
  const onCallSection = getOnCallSection()
  const bundlePricingSection = getBundlePricingSection({ annualPass })

  const faqData: Section[] = []
  if (sections?.length) {
    sections.forEach((section) => {
      if (section === 'general') faqData.push(generalSection)
      if (section === 'pricing') faqData.push(pricingSection)
      if (section === 'sessions') faqData.push(sessionsSection)
      if (section === 'bogo' && isBogoOfferEligible) faqData.push(bogoSection)
      if (section === 'guest-pass' && trial.isGuestPass)
        faqData.push(guestPassSection)
      if (section === 'free-trial' && trial.isPartnershipTrial)
        faqData.push(freeTrialSection)
      if (section === 'gifting') faqData.push(giftSection)
      // prcng-568_v1
      if (section === 'on-call') faqData.push(onCallSection)
      // prcng-568_v1
      if (section === 'bundle-&-pricing') faqData.push(bundlePricingSection)
    })
  }

  return { faqData }
}
